import type { RevertPreviewDto } from '@/services/types';

import { asEventType, asOperationType } from './mappers/enumGuards';
import type {
    RevertBlocker,
    RevertBlockerCode,
    RevertOperationResult,
    RevertPreview,
} from './revertTypes';

const BLOCKER_CODES: readonly RevertBlockerCode[] = [
    'OPERATION_NOT_LATEST',
    'CAGE_REUSED_SINCE',
    'STATE_MISMATCH',
    'SMOLT_REPORT_ON_DISCARDED_GROUP',
];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * The shape the preview mapper relies on. The generated client only casts the
 * response, so a payload that is not a preview is refused here, before mapping,
 * rather than crashing the dialog. The lists inside are mapped defensively.
 */
export function isRevertPreviewDto(value: unknown): value is RevertPreviewDto {
    return (
        isRecord(value) &&
        typeof value.operationId === 'number' &&
        typeof value.operationType === 'string' &&
        typeof value.operationDate === 'string' &&
        typeof value.revertible === 'boolean'
    );
}

function asBlockerCode(value: string): RevertBlockerCode {
    // An unknown code still blocks: it renders with the "state changed" copy.
    return BLOCKER_CODES.find((code) => code === value) ?? 'STATE_MISMATCH';
}

const numbers = (value: unknown): number[] =>
    Array.isArray(value) ? value.filter((item): item is number => typeof item === 'number') : [];

const records = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value) ? value.filter(isRecord) : [];

function mapBlocker(dto: Record<string, unknown>): RevertBlocker {
    return {
        code: asBlockerCode(typeof dto.code === 'string' ? dto.code : ''),
        fishGroupIds: numbers(dto.fishGroupIds),
        operationIds: numbers(dto.operationIds),
        cageIds: numbers(dto.cageIds),
    };
}

export function mapRevertPreviewToDomain(dto: RevertPreviewDto): RevertPreview {
    const blockers = records(dto.blockers).map(mapBlocker);
    return {
        operationId: dto.operationId,
        operationType: asOperationType(dto.operationType),
        operationDate: dto.operationDate,
        // Derived from the blockers too, so a preview that lists one is never offered.
        revertible: dto.revertible && blockers.length === 0,
        blockers,
        restoredFishGroups: dto.restoredFishGroups ?? [],
        discardedFishGroups: dto.discardedFishGroups ?? [],
        childManualEvents: (dto.childManualEvents ?? []).map((event) => ({
            id: event.id,
            fishGroupId: event.fishGroupId,
            eventType: asEventType(event.eventType),
            eventDate: event.eventDate,
        })),
    };
}

/**
 * Whether the revert response names every group it touched: both lists present
 * and every entry carrying an id. When it does not, the caller cannot know which
 * groups to refresh and has to refresh them all.
 */
export function isCompleteRevertResponse(data: unknown): boolean {
    const hasIds = (value: unknown) =>
        Array.isArray(value) &&
        value.every((group) => isRecord(group) && typeof group.id === 'number');
    return isRecord(data) && hasIds(data.restoredFishGroups) && hasIds(data.discardedFishGroups);
}

/** The ids of the groups in a list of `FishGroupDto`, skipping anything else. */
const groupIds = (value: unknown): number[] =>
    records(value)
        .map((group) => group.id)
        .filter((id): id is number => typeof id === 'number');

/**
 * Read the revert response without trusting its shape. The revert has already
 * committed by the time this runs, so a malformed body must not throw: the
 * missing parts come back empty, and the caller then refreshes the whole client.
 */
export function mapRevertOperationResultToDomain(
    data: unknown,
    operationId: number
): RevertOperationResult {
    const dto = isRecord(data) ? data : {};
    return {
        revertOperationId: typeof dto.revertOperationId === 'number' ? dto.revertOperationId : 0,
        revertedOperationId:
            typeof dto.revertedOperationId === 'number' ? dto.revertedOperationId : operationId,
        restoredFishGroupIds: groupIds(dto.restoredFishGroups),
        discardedFishGroupIds: groupIds(dto.discardedFishGroups),
    };
}
